import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { toast } from 'sonner';
import { Trash2, Plus, Edit3, Save, X, ImageIcon, CalendarClock, Clock, GripVertical, ChevronUp, ChevronDown } from 'lucide-react';

const blankShow = () => ({
  title: '',
  youtube_url: '',
  scheduled_at: '',
  duration_minutes: 60,
  duration_seconds: 0,
  description: '',
  image_url: '',
});

const formatDuration = (mins, secs) => {
  const m = parseInt(mins, 10) || 0;
  const s = parseInt(secs, 10) || 0;
  if (m === 0 && s === 0) return '0 sec';
  const parts = [];
  if (m > 0) parts.push(`${m} min`);
  if (s > 0) parts.push(`${s} sec`);
  return parts.join(' ');
};

const formatShowDateTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString('fr-FR', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

// Helper to convert ISO to datetime-local input format (YYYY-MM-DDTHH:MM)
const toLocalInput = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return '';
  }
};

const fromLocalInput = (local) => {
  if (!local) return '';
  try {
    return new Date(local).toISOString();
  } catch {
    return local;
  }
};

const ShowsManager = ({ token }) => {
  const [shows, setShows] = useState([]);
  const [draft, setDraft] = useState(blankShow());
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(blankShow());
  const [loading, setLoading] = useState(false);

  const fetchShows = async () => {
    try {
      const res = await axios.get(`${API}/shows`);
      setShows(res.data || []);
    } catch {
      toast.error('Échec du chargement des shows');
    }
  };

  useEffect(() => {
    fetchShows();
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!draft.title.trim() || !draft.youtube_url.trim() || !draft.scheduled_at) {
      toast.error('Titre, URL YouTube et date/heure sont requis');
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API}/shows`,
        {
          ...draft,
          scheduled_at: fromLocalInput(draft.scheduled_at),
          duration_minutes: parseInt(draft.duration_minutes, 10) || 0,
          duration_seconds: Math.max(0, Math.min(59, parseInt(draft.duration_seconds, 10) || 0)),
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Show ajouté');
      setDraft(blankShow());
      fetchShows();
    } catch {
      toast.error("Échec de l'ajout");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce show ?')) return;
    try {
      await axios.delete(`${API}/shows/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Show supprimé');
      fetchShows();
    } catch {
      toast.error('Échec de la suppression');
    }
  };

  const startEdit = (s) => {
    setEditingId(s.id);
    setEditDraft({
      title: s.title || '',
      youtube_url: s.youtube_url || '',
      scheduled_at: toLocalInput(s.scheduled_at),
      duration_minutes: s.duration_minutes ?? 60,
      duration_seconds: s.duration_seconds ?? 0,
      description: s.description || '',
      image_url: s.image_url || '',
    });
  };

  const saveEdit = async () => {
    try {
      await axios.put(
        `${API}/shows/${editingId}`,
        {
          ...editDraft,
          scheduled_at: fromLocalInput(editDraft.scheduled_at),
          duration_minutes: parseInt(editDraft.duration_minutes, 10) || 0,
          duration_seconds: Math.max(0, Math.min(59, parseInt(editDraft.duration_seconds, 10) || 0)),
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Show mis à jour');
      setEditingId(null);
      fetchShows();
    } catch {
      toast.error('Échec de la mise à jour');
    }
  };

  const handleReorder = async (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    const reordered = [...shows];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    setShows(reordered);
    try {
      await axios.put(
        `${API}/shows/reorder`,
        { show_ids: reordered.map((s) => s.id) },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Ordre mis à jour');
    } catch (error) {
      toast.error('Échec de la réorganisation');
      fetchShows();
    }
  };

  const handleMoveUp = (idx) => {
    if (idx === 0) return;
    handleReorder(idx, idx - 1);
  };

  const handleMoveDown = (idx) => {
    if (idx === shows.length - 1) return;
    handleReorder(idx, idx + 1);
  };

  return (
    <div className="resource-manager" data-testid="shows-manager">
      <Card className="add-video-card">
        <h3>Ajouter un show / une émission</h3>
        <p className="tab-subtitle" style={{ marginBottom: '1rem' }}>
          Chaque show est diffusé automatiquement à la date et heure programmées, et apparaît
          dans l&apos;onglet &laquo;&nbsp;Show TV&nbsp;&raquo; du menu public.
        </p>
        <form onSubmit={handleAdd} className="add-video-form">
          <Input
            placeholder="Titre du show (ex : Interview du Pasteur X)"
            value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            data-testid="show-title-input"
          />
          <Input
            placeholder="URL YouTube"
            value={draft.youtube_url}
            onChange={(e) => setDraft({ ...draft, youtube_url: e.target.value })}
            data-testid="show-url-input"
          />
          <div className="show-time-row">
            <div className="show-time-field">
              <label>Date et heure de diffusion</label>
              <Input
                type="datetime-local"
                value={draft.scheduled_at}
                onChange={(e) => setDraft({ ...draft, scheduled_at: e.target.value })}
                data-testid="show-datetime-input"
              />
            </div>
            <div className="show-time-field show-duration-field">
              <label>Durée</label>
              <div className="duration-inputs">
                <Input
                  type="number"
                  min="0"
                  placeholder="min"
                  value={draft.duration_minutes}
                  onChange={(e) => setDraft({ ...draft, duration_minutes: e.target.value })}
                  data-testid="show-duration-min-input"
                />
                <span className="duration-sep">min</span>
                <Input
                  type="number"
                  min="0"
                  max="59"
                  placeholder="sec"
                  value={draft.duration_seconds}
                  onChange={(e) => setDraft({ ...draft, duration_seconds: e.target.value })}
                  data-testid="show-duration-sec-input"
                />
                <span className="duration-sep">sec</span>
              </div>
            </div>
          </div>
          <Input
            placeholder="URL d'une image / vignette (facultatif)"
            value={draft.image_url}
            onChange={(e) => setDraft({ ...draft, image_url: e.target.value })}
            data-testid="show-image-input"
          />
          <textarea
            className="bio-textarea"
            rows={3}
            placeholder="Description du show (facultatif)"
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            data-testid="show-description-input"
          />
          <Button type="submit" disabled={loading} data-testid="add-show-button">
            <Plus size={18} />
            Programmer le show
          </Button>
        </form>
      </Card>

      <Card className="videos-list-card">
        <h3>Shows programmés ({shows.length})</h3>
        {shows.length === 0 ? (
          <div className="empty-state">
            <p>Aucun show programmé pour l&apos;instant. Ajoutez-en un ci-dessus.</p>
          </div>
        ) : (
          <div className="resource-list">
            {shows.map((s, idx) => {
              const start = new Date(s.scheduled_at);
              const totalMs = ((s.duration_minutes || 0) * 60 + (s.duration_seconds || 0)) * 1000;
              const end = new Date(start.getTime() + totalMs);
              const now = new Date();
              let status = 'upcoming';
              if (now >= start && now <= end) status = 'live';
              else if (now > end) status = 'past';
              return (
                <div
                  key={s.id}
                  className={`event-card show-card show-status-${status}`}
                  data-testid={`show-card-${idx}`}
                  draggable={editingId !== s.id}
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', idx);
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const from = parseInt(e.dataTransfer.getData('text/plain'));
                    if (!isNaN(from)) handleReorder(from, idx);
                  }}
                >
                  {editingId === s.id ? (
                    <div className="resource-edit" style={{ gridColumn: '1 / -1' }}>
                      <Input
                        value={editDraft.title}
                        onChange={(e) => setEditDraft({ ...editDraft, title: e.target.value })}
                      />
                      <Input
                        value={editDraft.youtube_url}
                        onChange={(e) => setEditDraft({ ...editDraft, youtube_url: e.target.value })}
                        placeholder="URL YouTube"
                      />
                      <div className="show-time-row">
                        <Input
                          type="datetime-local"
                          value={editDraft.scheduled_at}
                          onChange={(e) => setEditDraft({ ...editDraft, scheduled_at: e.target.value })}
                        />
                        <div className="duration-inputs">
                          <Input
                            type="number"
                            min="0"
                            value={editDraft.duration_minutes}
                            onChange={(e) => setEditDraft({ ...editDraft, duration_minutes: e.target.value })}
                            placeholder="min"
                          />
                          <span className="duration-sep">min</span>
                          <Input
                            type="number"
                            min="0"
                            max="59"
                            value={editDraft.duration_seconds}
                            onChange={(e) => setEditDraft({ ...editDraft, duration_seconds: e.target.value })}
                            placeholder="sec"
                          />
                          <span className="duration-sep">sec</span>
                        </div>
                      </div>
                      <Input
                        value={editDraft.image_url}
                        onChange={(e) => setEditDraft({ ...editDraft, image_url: e.target.value })}
                        placeholder="URL image"
                      />
                      <textarea
                        className="bio-textarea"
                        rows={3}
                        value={editDraft.description}
                        onChange={(e) => setEditDraft({ ...editDraft, description: e.target.value })}
                      />
                      <div className="resource-edit-actions">
                        <Button size="sm" onClick={saveEdit} data-testid={`save-show-${idx}`}>
                          <Save size={14} /> Enregistrer
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                          <X size={14} /> Annuler
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="event-image-wrap show-image-wrap">
                        <div className="show-drag-handle" title="Glisser pour réorganiser">
                          <GripVertical size={16} />
                        </div>
                        {s.image_url ? (
                          <img src={s.image_url} alt={s.title} className="event-image" />
                        ) : (
                          <div className="event-image placeholder">
                            <ImageIcon size={32} />
                          </div>
                        )}
                      </div>
                      <div className="event-body">
                        <h4>
                          {s.title}
                          {status === 'live' && <span className="show-badge show-badge-live">EN DIRECT</span>}
                          {status === 'past' && <span className="show-badge show-badge-past">Diffusé</span>}
                          {status === 'upcoming' && <span className="show-badge show-badge-upcoming">À venir</span>}
                        </h4>
                        <div className="event-meta">
                          <span>
                            <CalendarClock size={14} /> {formatShowDateTime(s.scheduled_at)}
                          </span>
                          <span>
                            <Clock size={14} /> {s.duration_minutes} min
                          </span>
                        </div>
                        {s.description && <p className="event-description">{s.description}</p>}
                      </div>
                      <div className="resource-actions">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleMoveUp(idx)}
                          disabled={idx === 0}
                          data-testid={`show-move-up-${idx}`}
                          className="move-button"
                          title="Monter"
                        >
                          <ChevronUp size={16} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleMoveDown(idx)}
                          disabled={idx === shows.length - 1}
                          data-testid={`show-move-down-${idx}`}
                          className="move-button"
                          title="Descendre"
                        >
                          <ChevronDown size={16} />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => startEdit(s)} data-testid={`edit-show-${idx}`}>
                          <Edit3 size={14} />
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDelete(s.id)}
                          data-testid={`delete-show-${idx}`}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
};

export default ShowsManager;
